/**
 * DataTimelineSection — vertical timeline of records sorted by date.
 * Supports: section title/description, connector/dot color, card style, Show More button.
 */

import React from 'react';
import { usePublicTableList } from '../../../hooks/usePublicTableView';
import DataSectionWrapper from '../DataSectionWrapper';
import { localeDateIn, parseInstant, zoneOf } from '../../../utils/utils';

/* A datetime column holds UTC with no zone of its own. Read it as that, and
   show it on the wall clock the record declares — `new Date()` would read it as
   the viewer's own and land an early-morning row on the previous day. */
function formatDate(val, timeZone) {
  if (!val) return '';
  const parsed = parseInstant(val);
  if (!parsed) return String(val);
  return localeDateIn(parsed, { year: 'numeric', month: 'short', day: 'numeric' }, timeZone);
}

export default function DataTimelineSection({ config, dataBinding, theme }) {
  const cfg       = config || {};
  const tableSlug = dataBinding?.table_slug;
  const fieldMap  = dataBinding?.field_mapping || {};
  const limit     = dataBinding?.limit || 20;
  const order     = cfg.order || 'desc';

  const connectorColor = cfg.connector_color || (theme?.primary || 'var(--primary-color)');
  const dotStyle_      = cfg.dot_style   || 'filled';
  const cardStyle_     = cfg.card_style  || 'default';
  const showDate       = cfg.show_date !== false;

  const { data, isLoading, isError } = usePublicTableList(tableSlug, { page_size: limit, sort_dir: order });
  const records = data?.records || data?.results || [];

  const viewMapping = data?.view_config?.field_mapping || {};
  const { title: titleField, description: descField, date: dateField } = { ...viewMapping, ...fieldMap };

  if (!tableSlug) return null;

  if (isLoading) {
    return (
      <DataSectionWrapper cfg={cfg} dataBinding={dataBinding}>
        <div className="text-center py-4"><span className="spinner-border spinner-border-sm" /></div>
      </DataSectionWrapper>
    );
  }

  if (isError || records.length === 0) {
    return (
      <DataSectionWrapper cfg={cfg} dataBinding={dataBinding}>
        <div className="text-muted text-center py-4">{isError ? 'Failed to load data.' : 'No records.'}</div>
      </DataSectionWrapper>
    );
  }

  const dotCss = dotStyle_ === 'outlined'
    ? { background: '#fff', border: `2px solid ${connectorColor}`, boxShadow: `0 0 0 2px ${connectorColor}40` }
    : { background: connectorColor, border: `2px solid #fff`, boxShadow: `0 0 0 2px ${connectorColor}40` };

  const cardCss =
    cardStyle_ === 'elevated' ? { background: '#fff', borderRadius: 10, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', padding: '12px 14px', marginBottom: 4 }
    : cardStyle_ === 'outlined' ? { background: '#fff', borderRadius: 8, border: '1px solid #e2e8f0', padding: '10px 12px', marginBottom: 4 }
    : cardStyle_ === 'minimal'  ? { marginBottom: 4 }
    : { marginBottom: 4 };

  return (
    <DataSectionWrapper cfg={cfg} dataBinding={dataBinding}>
      <div style={{ position: 'relative', paddingLeft: 32 }}>
        {/* Vertical connector line */}
        <div style={{ position: 'absolute', left: 12, top: 8, bottom: 8, width: 2, background: connectorColor, opacity: 0.25 }} />

        {records.map((record, idx) => {
          const title = titleField ? record[titleField] : Object.values(record)[0];
          const desc  = descField  ? record[descField]  : null;
          const date  = dateField  ? record[dateField]  : null;

          return (
            <div key={idx} style={{ position: 'relative', marginBottom: 24 }}>
              {/* Dot */}
              <div style={{
                position: 'absolute', left: -26, top: 5,
                width: 12, height: 12, borderRadius: '50%',
                ...dotCss,
              }} />

              <div style={cardCss}>
                {showDate && date && (
                  <div style={{ fontSize: 11, fontWeight: 600, color: connectorColor, marginBottom: 3 }}>
                    {formatDate(date, zoneOf(record))}
                  </div>
                )}
                <div className="fw-semibold" style={{ fontSize: '0.95rem' }}>{String(title || '')}</div>
                {desc && (
                  <div className="text-muted small mt-1">{String(desc)}</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </DataSectionWrapper>
  );
}
